// ─────────────────────────────────────────────────────────────────────────────
//  WATER MANAGEMENT DUMMY DATA
//  Society: Skyline Residency, Thane – 4 Blocks (A-D), 12 Floors, 192 Flats
// ─────────────────────────────────────────────────────────────────────────────

export const waterDummyData = {

  // ── Site Info ────────────────────────────────────────────
  site: {
    name: "Skyline Residency",
    location: "Thane, Maharashtra",
    totalFlats: 192,
    occupiedFlats: 168,
    totalResidents: 504,
    blocks: ["A", "B", "C", "D"],
    floorsPerBlock: 12,
    flatsPerFloor: 4,
  },

  // ── Executive Summary ────────────────────────────────────
  summary: {
    waterHealthScore: 72,
    totalWaterAvailable: 218000,   // litres
    totalCapacity: 300000,         // litres
    availabilityPct: 72.7,
    todayConsumption: 18400,       // litres
    yesterdayConsumption: 17800,
    monthlyConsumption: 422000,
    lastMonthConsumption: 395000,
    estimatedMonthlyCost: 84000,   // ₹
    criticalAlerts: 2,
    warningAlerts: 4,
    infoAlerts: 2,
    activePumps: 3,
    totalPumps: 5,
    consumptionPerCapita: 82,      // L/person/day
    daysOfWaterRemaining: 11.8,
    leakageSuspected: 2,
    recycledWaterPct: 22,          // % of total usage from STP
    aiInsights: [
      "Block B consumption up 23% vs last week. Possible leakage in riser pipe.",
      "Municipal supply has been irregular — 3 outages this month. Increase borewell extraction.",
      "Overhead Tank OHT-C at 91% — overflow risk. Stop refill pump for 2 hours.",
      "Night-time consumption (1–4 AM) is 340 L/hr, 18% above normal — likely hidden leak.",
      "Water cost forecast for this month: ₹88,400 — 5% above budget. Audit Block B.",
    ],
  },

  // ── Tanks ────────────────────────────────────────────────
  tanks: [
    {
      id: "GT-A", name: "Ground Tank – Block A", location: "Basement, Block A",
      type: "Ground", capacity: 50000, currentLevel: 34000, levelPct: 68,
      inflow: 420, outflow: 310, refillEta: "2h 20m",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "3 hrs ago", suppliedFrom: "Municipal",
    },
    {
      id: "GT-B", name: "Ground Tank – Block B", location: "Basement, Block B",
      type: "Ground", capacity: 50000, currentLevel: 41000, levelPct: 82,
      inflow: 380, outflow: 480, refillEta: "—",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "1 hr ago", suppliedFrom: "Municipal + Borewell",
    },
    {
      id: "OHT-A", name: "Overhead Tank – Block A", location: "Terrace, Block A",
      type: "Overhead", capacity: 20000, currentLevel: 7000, levelPct: 35,
      inflow: 280, outflow: 390, refillEta: "45m",
      overflowRisk: false, dryRunRisk: true, status: "Online",
      lastRefilled: "6 hrs ago", suppliedFrom: "GT-A",
    },
    {
      id: "OHT-B", name: "Overhead Tank – Block B", location: "Terrace, Block B",
      type: "Overhead", capacity: 20000, currentLevel: 14400, levelPct: 72,
      inflow: 320, outflow: 295, refillEta: "—",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "2 hrs ago", suppliedFrom: "GT-B",
    },
    {
      id: "OHT-C", name: "Overhead Tank – Block C", location: "Terrace, Block C",
      type: "Overhead", capacity: 20000, currentLevel: 18200, levelPct: 91,
      inflow: 410, outflow: 280, refillEta: "—",
      overflowRisk: true, dryRunRisk: false, status: "Online",
      lastRefilled: "30 min ago", suppliedFrom: "GT-C",
    },
    {
      id: "OHT-D", name: "Overhead Tank – Block D", location: "Terrace, Block D",
      type: "Overhead", capacity: 20000, currentLevel: 8800, levelPct: 44,
      inflow: 260, outflow: 340, refillEta: "1h 10m",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "4 hrs ago", suppliedFrom: "GT-D",
    },
    {
      id: "FR-1", name: "Fire Reserve Tank", location: "Basement, Common",
      type: "Reserve", capacity: 60000, currentLevel: 57000, levelPct: 95,
      inflow: 0, outflow: 0, refillEta: "—",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "2 days ago", suppliedFrom: "Municipal",
    },
    {
      id: "STP-1", name: "Treated Water Tank (STP)", location: "Common, EWS Bldg",
      type: "Treated", capacity: 30000, currentLevel: 17400, levelPct: 58,
      inflow: 180, outflow: 220, refillEta: "—",
      overflowRisk: false, dryRunRisk: false, status: "Online",
      lastRefilled: "Continuous", suppliedFrom: "STP Output",
    },
  ],

  // ── Water Sources ────────────────────────────────────────
  sources: [
    {
      id: "MUN", name: "Municipal Supply (MSEB)",
      type: "Municipal", status: "Active",
      dailyQuota: 80000, todayReceived: 68000, receivedPct: 85,
      pressurePsi: 28, qualityTds: 142, qualityPh: 7.2,
      lastSupply: "2 hrs ago", nextExpected: "6 PM", outagesThisMonth: 3,
      dependencyPct: 58, color: "#3b82f6",
    },
    {
      id: "BW1", name: "Borewell 1",
      type: "Borewell", status: "Active",
      dailyYield: 22000, todayExtracted: 18400, extractedPct: 84,
      waterLevel: 82, depthFt: 240, qualityTds: 380, qualityPh: 7.6,
      lastPumped: "1 hr ago", powerUsed: 18.4, dependencyPct: 28, color: "#10b981",
    },
    {
      id: "BW2", name: "Borewell 2",
      type: "Borewell", status: "Offline",
      dailyYield: 18000, todayExtracted: 0, extractedPct: 0,
      waterLevel: 0, depthFt: 200, qualityTds: 0, qualityPh: 0,
      lastPumped: "3 days ago (offline)", powerUsed: 0, dependencyPct: 0,
      offlineReason: "Pump maintenance", color: "#ef4444",
    },
    {
      id: "RWH", name: "Rainwater Harvesting",
      type: "Rainwater", status: "Seasonal",
      collectedThisMonth: 24000, storedCurrent: 11400, storagePct: 62,
      lastRainEvent: "Feb 22", rechargeRate: 0, dependencyPct: 8, color: "#06b6d4",
    },
    {
      id: "STP", name: "STP Recycled Water",
      type: "Recycled", status: "Active",
      dailyProduction: 8000, todayProduced: 6800, producedPct: 85,
      qualityTds: 210, usedFor: "Gardening + Flushing",
      dependencyPct: 6, savings: 3400, color: "#8b5cf6",
    },
  ],

  // ── Consumption Analytics ────────────────────────────────
  consumption: {
    hourlyToday: [
      { h: "12AM", litres: 120 }, { h: "1AM",  litres: 88  },
      { h: "2AM",  litres: 95  }, { h: "3AM",  litres: 82  },
      { h: "4AM",  litres: 140 }, { h: "5AM",  litres: 480 },
      { h: "6AM",  litres: 1820}, { h: "7AM",  litres: 2640},
      { h: "8AM",  litres: 2480}, { h: "9AM",  litres: 1680},
      { h: "10AM", litres: 980 }, { h: "11AM", litres: 740 },
      { h: "12PM", litres: 620 }, { h: "1PM",  litres: 560 },
      { h: "2PM",  litres: 480 }, { h: "3PM",  litres: 420 },
      { h: "4PM",  litres: 580 }, { h: "5PM",  litres: 940 },
      { h: "6PM",  litres: 1680}, { h: "7PM",  litres: 2240},
      { h: "8PM",  litres: 1980}, { h: "9PM",  litres: 1420},
      { h: "10PM", litres: 680 }, { h: "11PM", litres: 320 },
    ],
    weeklyTrend: [
      { d: "Mon", litres: 17200, cost: 3440 },
      { d: "Tue", litres: 18800, cost: 3760 },
      { d: "Wed", litres: 16400, cost: 3280 },
      { d: "Thu", litres: 19200, cost: 3840 },
      { d: "Fri", litres: 20100, cost: 4020 },
      { d: "Sat", litres: 22400, cost: 4480 },
      { d: "Sun", litres: 18400, cost: 3680 },
    ],
    monthlyComparison: [
      { month: "Dec 2025", litres: 382000, cost: 76400 },
      { month: "Jan 2026", litres: 395000, cost: 79000 },
      { month: "Feb 2026", litres: 422000, cost: 84400 },
    ],
    blockWise: [
      { block: "A", litres: 4820, residents: 132, perCapita: 81, pct: 26 },
      { block: "B", litres: 6240, residents: 128, perCapita: 94, pct: 34 },
      { block: "C", litres: 4180, residents: 124, perCapita: 78, pct: 23 },
      { block: "D", litres: 3160, residents: 120, perCapita: 72, pct: 17 },
    ],
    peakHours: ["6–9 AM", "7–10 PM"],
    seasonalTrend: [
      { month: "Mar", litres: 420 }, { month: "Apr", litres: 450 },
      { month: "May", litres: 490 }, { month: "Jun", litres: 380 },
      { month: "Jul", litres: 310 }, { month: "Aug", litres: 300 },
      { month: "Sep", litres: 330 }, { month: "Oct", litres: 360 },
      { month: "Nov", litres: 390 }, { month: "Dec", litres: 382 },
      { month: "Jan", litres: 395 }, { month: "Feb", litres: 422 },
    ],
  },

  // ── Flat Metering ────────────────────────────────────────
  flats: [
    // Block A
    { flatNo: "101-A", block: "A", floor: 1, occupants: 3, monthlyUsage: 8200, prevMonthUsage: 7900, dailyAvg: 274, efficiencyScore: 82, status: "Normal" },
    { flatNo: "201-A", block: "A", floor: 2, occupants: 4, monthlyUsage: 12400, prevMonthUsage: 11800, dailyAvg: 413, efficiencyScore: 68, status: "High" },
    { flatNo: "301-A", block: "A", floor: 3, occupants: 2, monthlyUsage: 5200, prevMonthUsage: 5600, dailyAvg: 173, efficiencyScore: 91, status: "Normal" },
    { flatNo: "401-A", block: "A", floor: 4, occupants: 4, monthlyUsage: 9800, prevMonthUsage: 9200, dailyAvg: 327, efficiencyScore: 75, status: "Normal" },
    { flatNo: "501-A", block: "A", floor: 5, occupants: 3, monthlyUsage: 7600, prevMonthUsage: 8100, dailyAvg: 253, efficiencyScore: 85, status: "Normal" },
    { flatNo: "601-A", block: "A", floor: 6, occupants: 5, monthlyUsage: 18400, prevMonthUsage: 14200, dailyAvg: 613, efficiencyScore: 42, status: "Critical" },
    // Block B
    { flatNo: "101-B", block: "B", floor: 1, occupants: 4, monthlyUsage: 14600, prevMonthUsage: 11800, dailyAvg: 487, efficiencyScore: 56, status: "High" },
    { flatNo: "201-B", block: "B", floor: 2, occupants: 3, monthlyUsage: 9400, prevMonthUsage: 9100, dailyAvg: 313, efficiencyScore: 78, status: "Normal" },
    { flatNo: "301-B", block: "B", floor: 3, occupants: 2, monthlyUsage: 6800, prevMonthUsage: 7200, dailyAvg: 227, efficiencyScore: 88, status: "Normal" },
    { flatNo: "401-B", block: "B", floor: 4, occupants: 5, monthlyUsage: 22100, prevMonthUsage: 18400, dailyAvg: 737, efficiencyScore: 28, status: "Critical" },
    { flatNo: "501-B", block: "B", floor: 5, occupants: 4, monthlyUsage: 11200, prevMonthUsage: 10800, dailyAvg: 373, efficiencyScore: 72, status: "Normal" },
    { flatNo: "601-B", block: "B", floor: 6, occupants: 3, monthlyUsage: 8900, prevMonthUsage: 8600, dailyAvg: 297, efficiencyScore: 80, status: "Normal" },
    // Block C
    { flatNo: "101-C", block: "C", floor: 1, occupants: 4, monthlyUsage: 10200, prevMonthUsage: 10400, dailyAvg: 340, efficiencyScore: 74, status: "Normal" },
    { flatNo: "201-C", block: "C", floor: 2, occupants: 2, monthlyUsage: 4800, prevMonthUsage: 5100, dailyAvg: 160, efficiencyScore: 95, status: "Excellent" },
    { flatNo: "301-C", block: "C", floor: 3, occupants: 3, monthlyUsage: 7900, prevMonthUsage: 8200, dailyAvg: 263, efficiencyScore: 84, status: "Normal" },
    { flatNo: "401-C", block: "C", floor: 4, occupants: 4, monthlyUsage: 11800, prevMonthUsage: 11200, dailyAvg: 393, efficiencyScore: 70, status: "Normal" },
    // Block D
    { flatNo: "101-D", block: "D", floor: 1, occupants: 3, monthlyUsage: 7400, prevMonthUsage: 7800, dailyAvg: 247, efficiencyScore: 87, status: "Normal" },
    { flatNo: "201-D", block: "D", floor: 2, occupants: 4, monthlyUsage: 9100, prevMonthUsage: 8800, dailyAvg: 303, efficiencyScore: 77, status: "Normal" },
    { flatNo: "301-D", block: "D", floor: 3, occupants: 2, monthlyUsage: 5400, prevMonthUsage: 5800, dailyAvg: 180, efficiencyScore: 92, status: "Excellent" },
    { flatNo: "401-D", block: "D", floor: 4, occupants: 5, monthlyUsage: 13200, prevMonthUsage: 12400, dailyAvg: 440, efficiencyScore: 61, status: "High" },
  ],

  // ── Billing ──────────────────────────────────────────────
  billing: {
    month: "February 2026",
    totalGenerated: 840000,
    collected: 714000,
    collectionRate: 85,
    pending: 126000,
    overdueFlats: 12,
    overdueAmount: 58400,
    totalFlats: 168,
    billedFlats: 168,
    slabs: [
      { range: "0 – 5 KL",  rate: 0,   unit: "Free (basic allocation)" },
      { range: "5 – 10 KL", rate: 10,  unit: "₹10/KL" },
      { range: "10 – 20 KL",rate: 20,  unit: "₹20/KL" },
      { range: "> 20 KL",   rate: 35,  unit: "₹35/KL" },
    ],
    fixedCharge: 150,
    tankerCostShare: 8200,
    gstPct: 18,
    latePenaltyPct: 2,
    sampleBills: [
      { flatNo: "601-A", block: "A", occupants: 5, usage: 18.4, slabAmt: 3060, fixed: 150, tanker: 280, gst: 585, total: 4075, status: "Paid" },
      { flatNo: "401-B", block: "B", occupants: 5, usage: 22.1, slabAmt: 4535, fixed: 150, tanker: 280, gst: 862, total: 5827, status: "Overdue" },
      { flatNo: "201-A", block: "A", occupants: 4, usage: 12.4, slabAmt: 1480, fixed: 150, tanker: 280, gst: 344, total: 2254, status: "Paid" },
      { flatNo: "101-B", block: "B", occupants: 4, usage: 14.6, slabAmt: 1920, fixed: 150, tanker: 280, gst: 431, total: 2781, status: "Overdue" },
      { flatNo: "201-C", block: "C", occupants: 2, usage: 4.8,  slabAmt: 0,    fixed: 150, tanker: 280, gst: 78,  total: 508,  status: "Paid" },
    ],
    collectionTrend: [
      { month: "Nov", generated: 810000, collected: 728000 },
      { month: "Dec", generated: 836000, collected: 745000 },
      { month: "Jan", generated: 828000, collected: 706000 },
      { month: "Feb", generated: 840000, collected: 714000 },
    ],
  },

  // ── Leakage & Anomaly Detection ──────────────────────────
  leakage: {
    overallRisk: "Moderate",
    totalSuspectedLoss: 840,   // litres/day
    estimatedCostLoss: 168,    // ₹/day
    anomalies: [
      {
        id: "LK-001", severity: "Critical",
        type: "Continuous Low Flow (Hidden Leak)",
        location: "Block B – Riser Pipe (Floor 3-6)",
        probability: 87, flow: "0.6 L/min continuous",
        detectedAt: "02:14 AM", duration: "18 hrs",
        status: "Under Investigation",
        recommendation: "Inspect riser pipe joints B3 to B6. Shut secondary valve.",
      },
      {
        id: "LK-002", severity: "Critical",
        type: "Night-time Abnormal Usage",
        location: "Block B – Flat 401-B",
        probability: 92, flow: "4.8 L/min (12 AM–4 AM)",
        detectedAt: "12:04 AM", duration: "4 hrs",
        status: "Alert Sent",
        recommendation: "Contact resident. Inspect toilet cistern and tap fittings.",
      },
      {
        id: "LK-003", severity: "Warning",
        type: "Sudden High Spike",
        location: "Block A – Common Area (Garden)",
        probability: 65, flow: "Spike: 18 L/min for 22 min",
        detectedAt: "09:32 AM", duration: "22 min",
        status: "Resolved",
        recommendation: "Garden sprinkler valve was stuck open. Resolved by maintenance.",
      },
      {
        id: "LK-004", severity: "Warning",
        type: "Tank Overflow Pattern",
        location: "OHT-C (Block C Terrace)",
        probability: 78, flow: "Overflow detected – 340 L wasted",
        detectedAt: "06:48 AM", duration: "8 min",
        status: "Auto-stopped",
        recommendation: "Float valve in OHT-C needs replacement. Scheduled for today.",
      },
      {
        id: "LK-005", severity: "Info",
        type: "Elevated Consumption",
        location: "Block D – Floor 4",
        probability: 48, flow: "140% of average for this floor",
        detectedAt: "Yesterday", duration: "Ongoing",
        status: "Monitoring",
        recommendation: "Usage spiked after new residents moved in Flat 401-D.",
      },
    ],
    nightFlowBaseline: 80,    // L/hr normal at night
    nightFlowActual: 340,     // L/hr detected
    continuousFlowAlerts: 2,
    overflowEvents: 1,
  },

  // ── Pumps & Motors ───────────────────────────────────────
  pumps: [
    {
      id: "PMP-01", name: "Main Pump 1 (Block A/B)",
      location: "Pump Room, Basement A",
      status: "Running", type: "Submersible",
      runningHoursToday: 3.4, totalRunningHours: 8420,
      powerKw: 5.5, currentAmps: 18.2, voltageV: 415,
      vibration: "Normal", flowLpm: 420, pressureBar: 3.2,
      efficiency: 88, temperature: 62,
      lastMaintenance: "12 days ago", nextMaintenance: "In 18 days",
      alerts: [], healthScore: 86,
    },
    {
      id: "PMP-02", name: "Main Pump 2 (Block C/D)",
      location: "Pump Room, Basement C",
      status: "Running", type: "Submersible",
      runningHoursToday: 2.8, totalRunningHours: 7210,
      powerKw: 5.5, currentAmps: 17.8, voltageV: 412,
      vibration: "Normal", flowLpm: 390, pressureBar: 3.0,
      efficiency: 84, temperature: 58,
      lastMaintenance: "8 days ago", nextMaintenance: "In 22 days",
      alerts: [], healthScore: 91,
    },
    {
      id: "PMP-03", name: "Borewell Pump",
      location: "Borewell 1, North Compound",
      status: "Running", type: "Borewell",
      runningHoursToday: 4.2, totalRunningHours: 12840,
      powerKw: 7.5, currentAmps: 24.4, voltageV: 415,
      vibration: "Moderate", flowLpm: 280, pressureBar: 4.8,
      efficiency: 71, temperature: 74,
      lastMaintenance: "38 days ago", nextMaintenance: "Overdue",
      alerts: ["Overdue maintenance", "Elevated vibration detected"],
      healthScore: 58,
    },
    {
      id: "PMP-04", name: "Booster Pump (OHT-A/D)",
      location: "Pump Room, Basement A",
      status: "Standby", type: "Centrifugal",
      runningHoursToday: 0.4, totalRunningHours: 3240,
      powerKw: 3.7, currentAmps: 0, voltageV: 415,
      vibration: "—", flowLpm: 0, pressureBar: 0,
      efficiency: 90, temperature: 32,
      lastMaintenance: "5 days ago", nextMaintenance: "In 25 days",
      alerts: [], healthScore: 94,
    },
    {
      id: "PMP-05", name: "STP Transfer Pump",
      location: "STP Plant, EWS Building",
      status: "Offline", type: "Centrifugal",
      runningHoursToday: 0, totalRunningHours: 2180,
      powerKw: 2.2, currentAmps: 0, voltageV: 0,
      vibration: "—", flowLpm: 0, pressureBar: 0,
      efficiency: 0, temperature: 28,
      lastMaintenance: "—", nextMaintenance: "Under Service",
      alerts: ["Offline – Capacitor replacement in progress"],
      healthScore: 42,
    },
  ],

  // ── Alerts Center ─────────────────────────────────────────
  alerts: [
    { id: "WA-001", severity: "Critical", category: "Overflow",      location: "OHT-C, Block C", message: "Overhead tank at 91%. Overflow imminent. Stop refill pump.", time: "08:14 AM", assignee: "Plumber Team", status: "Open" },
    { id: "WA-002", severity: "Critical", category: "Leakage",       location: "Block B, Riser Pipe", message: "Continuous 0.6 L/min flow for 18 hrs. Probable hidden leak.", time: "02:14 AM", assignee: "Maintenance", status: "Investigating" },
    { id: "WA-003", severity: "Warning",  category: "Low Tank",      location: "OHT-A, Block A", message: "Overhead tank A at 35%. Enable pump to refill urgently.", time: "09:30 AM", assignee: "Operator", status: "Action Taken" },
    { id: "WA-004", severity: "Warning",  category: "Pump Health",   location: "PMP-03, Borewell", message: "Elevated vibration and overdue maintenance on borewell pump.", time: "10:02 AM", assignee: "Maintenance", status: "Scheduled" },
    { id: "WA-005", severity: "Warning",  category: "High Usage",    location: "Flat 401-B, Block B", message: "22.1 KL usage this month – 78% above average.", time: "Yesterday", assignee: "Admin", status: "Notified" },
    { id: "WA-006", severity: "Warning",  category: "High Usage",    location: "Flat 601-A, Block A", message: "18.4 KL usage – 48% above average. Inspect flat.", time: "Yesterday", assignee: "Admin", status: "Notified" },
    { id: "WA-007", severity: "Info",     category: "Supply",        location: "Municipal Supply", message: "Municipal supply restored after 2-hr outage. Normal pressure now.", time: "07:45 AM", assignee: "—", status: "Closed" },
    { id: "WA-008", severity: "Info",     category: "Pump",          location: "PMP-01, Main", message: "Main Pump 1 started automatically. OHT-A refill initiated.", time: "06:30 AM", assignee: "—", status: "Closed" },
  ],

  // ── Reports ──────────────────────────────────────────────
  reports: [
    { id: "RPT-001", name: "Water Usage Report",      description: "Block-wise & flat-wise monthly consumption summary", period: "Feb 2026", size: "284 KB", format: "PDF + Excel" },
    { id: "RPT-002", name: "Billing Summary",         description: "Complete billing with slab breakdown, GST, and overdue list", period: "Feb 2026", size: "142 KB", format: "PDF" },
    { id: "RPT-003", name: "Tanker Usage Report",     description: "Tanker deliveries log with vendor and quantity details", period: "Feb 2026", size: "68 KB",  format: "Excel" },
    { id: "RPT-004", name: "Leakage Audit Report",    description: "AI-detected anomalies, leakage events and resolutions", period: "Feb 2026", size: "96 KB",  format: "PDF" },
    { id: "RPT-005", name: "Pump Operation Report",   description: "Running hours, power consumption, maintenance log", period: "Feb 2026", size: "112 KB", format: "PDF + Excel" },
    { id: "RPT-006", name: "Government Compliance Log", description: "Water source usage as per CGWA/State board requirements", period: "Q4 2025", size: "220 KB", format: "PDF" },
    { id: "RPT-007", name: "Year-over-Year Analysis", description: "Annual consumption and cost comparison across 3 years", period: "2023–2026", size: "380 KB", format: "PDF" },
  ],
};
